import React, { ErrorInfo, ReactNode, useState, useEffect } from "react";
import { connect } from "react-redux";
import { Location } from "history";
import { getUser } from "metabase/selectors/user";
import ScrollToTop from "metabase/hoc/ScrollToTop";
import {
  Archived,
  GenericError,
  NotFound,
  Unauthorized,
} from "metabase/containers/ErrorPages";
import UndoListing from "metabase/containers/UndoListing";

import {
  getErrorPage,
  getIsAdminApp,
  getIsAppBarVisible,
  getIsNavBarVisible,
} from "metabase/selectors/app";
import { setErrorPage, setChannel } from "metabase/redux/app";
import { useOnMount } from "metabase/hooks/use-on-mount";
import { initializeIframeResizer } from "metabase/lib/dom";

import Navbar from "metabase/nav/containers/Navbar";
import StatusListing from "metabase/status/containers/StatusListing";
import { ContentViewportContext } from "metabase/core/context/ContentViewportContext";

import { AppErrorDescriptor, State } from "metabase-types/store";

import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";

import { AppContainer, AppContent, AppContentContainer } from "./App.styled";
import ABLayout from "./ab/components/GaLayout";
import cx from "classnames";
import { ConfigProvider, theme } from "antd";
import { isDark } from "./dashboard/components/utils/dark";
import getThemeConfig from "./theme-helper";
import * as MetabaseAnalytics from "metabase/lib/analytics";
import { isProduction } from "metabase/env";
const getErrorComponent = ({ status, data, context }: AppErrorDescriptor) => {
  if (status === 403 || data?.error_code === "unauthorized") {
    return <Unauthorized />;
  }
  if (status === 404 || data?.error_code === "not-found") {
    return <NotFound />;
  }
  if (data?.error_code === "archived" && context === "dashboard") {
    return <Archived entityName="dashboard" linkTo="/dashboards/archive" />;
  }
  if (data?.error_code === "archived" && context === "query-builder") {
    return <Archived entityName="question" linkTo="/questions/archive" />;
  }
  return <GenericError details={data?.message} />;
};

interface AppStateProps {
  user: any;
  errorPage: AppErrorDescriptor | null;
  isAdminApp: boolean;
  bannerMessageDescriptor?: string;
  isAppBarVisible: boolean;
  isNavBarVisible: boolean;
}

interface AppDispatchProps {
  onError: (error: unknown) => void;
  setChannel: any;
}

interface AppRouterOwnProps {
  location: Location;
  children: ReactNode;
}

type AppProps = AppStateProps & AppDispatchProps & AppRouterOwnProps;

const mapStateToProps = (
  state: State,
  props: AppRouterOwnProps,
): AppStateProps => ({
  user: getUser(state),
  errorPage: getErrorPage(state),
  isAdminApp: getIsAdminApp(state, props),
  isAppBarVisible: getIsAppBarVisible(state, props),
  isNavBarVisible: getIsNavBarVisible(state, props),
});

const mapDispatchToProps: AppDispatchProps = {
  onError: setErrorPage,
  setChannel,
};

class ErrorBoundary extends React.Component<{
  onError: (errorInfo: ErrorInfo) => void;
}> {
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.props.onError(errorInfo);
  }

  render() {
    return this.props.children;
  }
}
const usePageTracking = () => {
  const location = window.location.pathname;

  useEffect(() => {
    setTimeout(() => {
      MetabaseAnalytics.handleQueueEvent()
    }, 3000)
  }, [location]);
};

function App({
  errorPage,
  isAdminApp,
  isAppBarVisible,
  isNavBarVisible,
  children,
  onError,
  location,
  setChannel,
  user,
}: AppProps) {
  const [viewportElement, setViewportElement] = useState<HTMLElement | null>();
  const hideScrollbar = location.pathname === "/";
  const isAdmin = location.pathname.startsWith("/admin");

  useOnMount(() => {
    initializeIframeResizer();
    MetabaseAnalytics.init()
    setTimeout(() => {
      Sentry.init({
        dsn: isProduction ? "https://668561d74b9c11c6fa8d4b0f6784c2c6@o4508956662693888.ingest.us.sentry.io/4508956665839616"
          : "https://f7c15cfe6a85aa368f8f124c21e23bd3@o4508918805168128.ingest.de.sentry.io/4508918816637008",
        integrations: [ new Integrations.BrowserTracing() as any ],

        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 1.0,
      });
    }, 1000)
  });
  usePageTracking();

  return (
    <React.Fragment>
      <ConfigProvider theme={getThemeConfig()}>
        <ErrorBoundary onError={onError}>
          <ScrollToTop>
            <AppContainer className="spread">
              <ABLayout>
                <AppContentContainer
                  // className={ "dark"}
                  className={isDark() ? "dark full-height" : "full-height"}
                  isAdminApp={isAdminApp}
                >
                  {isNavBarVisible && <Navbar location={location} />}
                  <AppContent
                    className={cx({ "scroll-hide-all": hideScrollbar })}
                    id="app-content"
                    ref={setViewportElement}
                    key={`${user?.id}`}
                    style={{backgroundColor: isAdmin ? "white" : "#101318"}}
                  >
                    <ContentViewportContext.Provider
                      value={viewportElement ?? null}
                    >
                      {errorPage ? getErrorComponent(errorPage) : children}
                    </ContentViewportContext.Provider>
                  </AppContent>
                  <UndoListing />
                  <StatusListing />
                </AppContentContainer>
              </ABLayout>
            </AppContainer>
          </ScrollToTop>
        </ErrorBoundary>
      </ConfigProvider>
    </React.Fragment>
  );
}

export default connect<AppStateProps, unknown, AppRouterOwnProps, State>(
  mapStateToProps,
  mapDispatchToProps,
)(App);
